import { useState, useEffect } from 'react'
import { Loader2, Download } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { FileUploadDropzone } from '@/components/PlanWizard/FileUploadDropzone'
import { ImportResultsSummary } from '@/components/PlanDetail/ImportResultsSummary'
import { useHistoricalDataUpload } from '@/hooks/useHistoricalData'
import { useProductListItems, useProductList } from '@/hooks/useProductLists'
import { useBulkImportLines } from '@/hooks/usePlanDetail'
import { toast } from '@/stores/useToastStore'
import { downloadTemplate } from '@/utils/downloadTemplate'

type Step = 'upload' | 'processing' | 'preview' | 'importing'

interface ImportItemsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  planId: string
  versionId: string
  onImportComplete: () => void
}

export function ImportItemsModal({
  open,
  onOpenChange,
  planId,
  versionId,
  onImportComplete,
}: ImportItemsModalProps) {
  const [step, setStep] = useState<Step>('upload')
  const [productListId, setProductListId] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(5)

  const uploadState = useHistoricalDataUpload()
  const resetUpload = uploadState.reset
  const bulkImport = useBulkImportLines(planId, versionId)

  const {
    data: previewData,
    isLoading: isPreviewLoading,
    error: previewError,
  } = useProductListItems(
    uploadState.isCompleted ? productListId : null,
    page,
    pageSize
  )

  // Fetch product list details for summary stats
  const { data: productListData } = useProductList(
    uploadState.isCompleted ? productListId : null
  )

  const resetBulkImport = bulkImport.reset

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setStep('upload')
      setProductListId(null)
      setPage(1)
      resetUpload()
      resetBulkImport()
    }
  }, [open, resetUpload, resetBulkImport])

  // Transition to preview when processing completes
  useEffect(() => {
    if (uploadState.isCompleted && productListId) {
      setStep('preview')
    }
  }, [uploadState.isCompleted, productListId])

  // Handle file selection and upload
  const handleFileSelect = async (file: File) => {
    try {
      setStep('processing')
      const result = await uploadState.upload(file)
      setProductListId(result.productListId)
    } catch {
      setStep('upload')
    }
  }

  // Handle import confirmation
  const handleAddItems = () => {
    if (!productListId) return

    setStep('importing')
    bulkImport.start(productListId)
  }

  // Handle bulk import completion or error via SSE / polling
  useEffect(() => {
    if (bulkImport.isCompleted) {
      toast.success('Items imported successfully')
      onOpenChange(false)
      onImportComplete()
    } else if (bulkImport.isError && step === 'importing') {
      toast.error(`Import failed: ${bulkImport.error}`)
      setStep('preview')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkImport.isCompleted, bulkImport.isError])

  // Handle discard
  const handleDiscard = () => {
    onOpenChange(false)
  }

  // Prevent closing during processing or importing
  const handleOpenChange = (newOpen: boolean) => {
    if (step === 'processing' || step === 'importing') {
      return
    }
    onOpenChange(newOpen)
  }

  // Calculate progress for the processing step
  const getProgressValue = () => {
    if (uploadState.isUploading) return 25
    if (uploadState.isCreating) return 50
    if (uploadState.isProcessing) return Math.max(50, uploadState.progress)
    return uploadState.progress
  }

  const hasItems = previewData && previewData.totalCount > 0
  const hasIssues = productListData && productListData.invalid_rows > 0

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={step === 'preview' && hasIssues ? 'max-w-3xl' : 'max-w-2xl'}
      >
        <DialogHeader>
          <DialogTitle>Import Items</DialogTitle>
          <DialogDescription>
            {step === 'upload' &&
              'Upload an Excel file to import items into your plan.'}
            {step === 'processing' && 'Processing your file...'}
            {step === 'preview' && 'Review the import results.'}
            {step === 'importing' && 'Adding items to your plan...'}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 overflow-y-auto min-h-0 max-h-[60vh]">
          {/* Upload Step */}
          {step === 'upload' && (
            <div>
              <FileUploadDropzone
                onFileSelect={handleFileSelect}
                isLoading={false}
                error={uploadState.error}
              />
              <p className="text-[13px] text-[#556374] mt-3">
                <button
                  onClick={() => {
                    downloadTemplate().catch(() => {
                      toast.error('Failed to download template')
                    })
                  }}
                  className="text-primary hover:underline cursor-pointer inline-flex items-center gap-1"
                >
                  <Download className="h-3 w-3" />
                  Download template
                </button>
              </p>
            </div>
          )}

          {/* Processing Step */}
          {step === 'processing' && (
            <div className="flex flex-col items-center gap-4 py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <div className="w-full max-w-md space-y-2">
                <Progress value={getProgressValue()} className="h-2" />
                <p className="text-center text-sm text-muted-foreground">
                  {uploadState.isUploading && 'Uploading file...'}
                  {uploadState.isCreating && 'Creating product list...'}
                  {uploadState.isProcessing &&
                    `Processing items... ${uploadState.progress}%`}
                </p>
              </div>
              {uploadState.fileName && (
                <p className="text-sm text-muted-foreground">
                  File: {uploadState.fileName}
                </p>
              )}
            </div>
          )}

          {/* Preview Step — with results summary when product list data is available */}
          {step === 'preview' && (
            <>
              {previewError ? (
                <div className="px-6 py-12 text-center">
                  <p className="text-sm text-red-500">
                    Failed to load preview: {previewError.message}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    onClick={() => setStep('upload')}
                  >
                    Try Again
                  </Button>
                </div>
              ) : productListData ? (
                <ImportResultsSummary
                  productList={productListData}
                  validPagination={{
                    items: previewData?.items ?? [],
                    totalCount: previewData?.totalCount ?? 0,
                    page,
                    pageSize,
                    totalPages: previewData?.totalPages ?? 0,
                    isLoading: isPreviewLoading,
                    onPageChange: setPage,
                    onPageSizeChange: setPageSize,
                  }}
                />
              ) : (
                <div className="px-6 py-12 text-center">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2 text-[#556374]" />
                  <span className="text-[14px] text-[#556374]">
                    Loading results...
                  </span>
                </div>
              )}
            </>
          )}

          {/* Importing Step */}
          {step === 'importing' && (
            <div className="flex flex-col items-center gap-4 py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <div className="w-full max-w-md space-y-2">
                <Progress value={bulkImport.progress} className="h-2" />
                <p className="text-center text-sm text-muted-foreground">
                  {bulkImport.isStarting && 'Starting import...'}
                  {bulkImport.isProcessing &&
                    `Importing items... ${bulkImport.progress}%`}
                </p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          {step === 'upload' && (
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
          )}

          {step === 'preview' && (
            <>
              <Button variant="outline" onClick={handleDiscard}>
                Discard
              </Button>
              <Button onClick={handleAddItems} disabled={!hasItems}>
                Add Items {hasItems && `(${previewData?.totalCount})`}
              </Button>
            </>
          )}

          {/* No buttons during processing or importing */}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
